/** Stable selectors for `useStore(sel)` and `store.subscribe(sel, cb)`. */
import type { Store } from './store';

export const selectMe = (s: Store) => s.session.me;
export const selectSeat = (s: Store) => s.session.seat;
export const selectPlatform = (s: Store) => s.session.platform;
export const selectToken = (s: Store) => s.session.token;
export const selectParticipants = (s: Store) => s.session.participants;
export const selectSpeaking = (s: Store) => s.session.speaking;
export const selectMutedUsers = (s: Store) => s.session.muted;
export const selectConnection = (s: Store) => s.session.connection;
export const selectSessionError = (s: Store) => s.session.error;

export const selectTable = (s: Store) => s.table;
export const selectPhase = (s: Store) => s.table?.phase ?? 'lobby';
export const selectTurn = (s: Store) => s.table?.turn ?? -1;
export const selectSeats = (s: Store) => s.table?.seats;
export const selectTeams = (s: Store) => s.table?.teams;
export const selectTimer = (s: Store) => s.table?.timer;
export const selectPrivateView = (s: Store) => s.privateView;
export const selectHand = (s: Store) => s.privateView?.hand;
export const selectLegal = (s: Store) => s.privateView?.legal;
export const selectIsMyTurn = (s: Store) =>
  s.session.seat != null && s.table != null && s.table.turn === s.session.seat;
export const selectIsHost = (s: Store) =>
  s.session.me != null && s.table != null && s.table.hostPlayerId === s.session.me.id;

export const selectScreen = (s: Store) => s.presentation.screen;
export const selectLayoutMode = (s: Store) => s.presentation.layoutMode;
export const selectOrientation = (s: Store) => s.presentation.orientation;
export const selectPerfTier = (s: Store) => s.presentation.perfTier;
export const selectCueBusy = (s: Store) => s.presentation.cueBusy;
export const selectSubtitle = (s: Store) => s.presentation.subtitle;
export const selectTakeover = (s: Store) => s.presentation.takeover;
export const selectAnchors = (s: Store) => s.presentation.anchors;
export const selectDisplayedScores = (s: Store) => s.presentation.displayedScores;
export const selectSlamCharge = (s: Store) => s.presentation.slamCharge;

export const selectSettings = (s: Store) => s.settings;
export const selectVolume = (s: Store) => s.settings.volume;
export const selectMotion = (s: Store) => s.settings.motion;
export const selectPerfOverride = (s: Store) => s.settings.perfOverride;
export const selectSubtitlesOn = (s: Store) => s.settings.subtitles;
export const selectMutedEmotes = (s: Store) => s.settings.mutedEmotes;
export const selectTutorialDone = (s: Store) => s.settings.tutorialDone;
export const selectEmoteSlots = (s: Store) => s.settings.emoteSlots;

export const selectProgress = (s: Store) => s.progress;
export const selectMyProfile = (s: Store) => s.progress.me;
export const selectLastProgress = (s: Store) => s.progress.lastMatch;
export const selectProfileLoading = (s: Store) => s.progress.loading;
/**
 * My level for a badge, or null for none.
 *
 * The seat is the source of truth while seated — the room pushes `SeatMirror.level` and it is the
 * same number every other player sees — and the fetched profile is the fallback for the entry menu,
 * where there is no table yet. Zero means "no finished match", which `LevelBadge` renders as
 * nothing, so it is normalised to null here rather than at each call site.
 */
export const selectMyLevel = (s: Store): number | null => {
  const seated = s.session.me ? s.table?.seats.find((seat) => seat.playerId === s.session.me!.id) : undefined;
  return seated?.level || s.progress.me?.level || null;
};

/** Effective perf tier: manual override beats the governor. */
export const selectEffectivePerfTier = (s: Store) =>
  s.settings.perfOverride === 'auto' ? s.presentation.perfTier : s.settings.perfOverride;
